/**
 * A pool of workers with request/response correlation.
 *
 * Generalised from the single-in-flight worker driver in the sibling smartmaps
 * project: here each worker may hold several jobs, and a job can be cancelled
 * so the worker stops paying for a fetch the player has already walked away
 * from.
 */

import type { WorkerInbound, WorkerOutbound } from './protocol.js';

interface PendingJob<R> {
  resolve: (value: R) => void;
  reject: (error: unknown) => void;
  worker: Worker;
  onAbort?: () => void;
  signal?: AbortSignal;
}

export class WorkerPool<P, R> {
  private readonly workers: Worker[] = [];
  private readonly loads: number[] = [];
  private readonly pending = new Map<number, PendingJob<R>>();
  private nextId = 1;
  private disposed = false;

  constructor(factory: () => Worker, size: number) {
    for (let i = 0; i < Math.max(1, size); i++) {
      const worker = factory();
      worker.addEventListener('message', (event: MessageEvent<WorkerOutbound<R>>) => {
        this.settle(event.data);
      });
      worker.addEventListener('error', (event) => {
        // A worker-level error has no job id attached, so fail everything that
        // worker was holding rather than leaving those promises dangling.
        for (const [id, job] of [...this.pending]) {
          if (job.worker === worker) {
            this.pending.delete(id);
            job.reject(new Error(`Worker error: ${event.message}`));
          }
        }
      });
      this.workers.push(worker);
      this.loads.push(0);
    }
  }

  get inFlight(): number {
    return this.pending.size;
  }

  run(payload: P, transfer: Transferable[] = [], signal?: AbortSignal): Promise<R> {
    if (this.disposed) return Promise.reject(new Error('WorkerPool disposed'));
    if (signal?.aborted) return Promise.reject(abortError());

    const index = this.leastBusy();
    const worker = this.workers[index];
    const id = this.nextId++;
    this.loads[index]++;

    return new Promise<R>((resolve, reject) => {
      const job: PendingJob<R> = { resolve, reject, worker, signal };

      if (signal !== undefined) {
        job.onAbort = () => {
          if (!this.pending.has(id)) return;
          this.pending.delete(id);
          this.loads[index]--;
          // Tell the worker to stop; it will not reply for this id.
          worker.postMessage({ id, cancel: true } satisfies WorkerInbound<P>);
          reject(abortError());
        };
        signal.addEventListener('abort', job.onAbort, { once: true });
      }

      this.pending.set(id, job);
      worker.postMessage({ id, payload } satisfies WorkerInbound<P>, transfer);
    });
  }

  private settle(message: WorkerOutbound<R>): void {
    const job = this.pending.get(message.id);
    if (job === undefined) return; // cancelled, or a late reply after dispose
    this.pending.delete(message.id);
    const index = this.workers.indexOf(job.worker);
    if (index >= 0) this.loads[index]--;
    if (job.onAbort !== undefined && job.signal !== undefined) {
      job.signal.removeEventListener('abort', job.onAbort);
    }
    if (message.ok) job.resolve(message.result);
    else job.reject(message.aborted ? abortError() : new Error(message.error));
  }

  private leastBusy(): number {
    let best = 0;
    for (let i = 1; i < this.loads.length; i++) {
      if (this.loads[i] < this.loads[best]) best = i;
    }
    return best;
  }

  dispose(): void {
    this.disposed = true;
    for (const [, job] of this.pending) job.reject(abortError());
    this.pending.clear();
    for (const worker of this.workers) worker.terminate();
    this.workers.length = 0;
  }
}

function abortError(): Error {
  const error = new Error('Aborted');
  error.name = 'AbortError';
  return error;
}
